import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { ArrowLeft, Printer } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  useCompanyProfile,
  formatOrgNumber,
  formatPhone,
  formatWebsite,
} from "@/hooks/use-company-profile";
import { formatDate } from "@/lib/format";
import { ElectroCustomerPdf } from "@/features/electro-docs/ElectroCustomerPdf";

export const Route = createFileRoute("/_authenticated/projects/$id_/documentation/pdf")({
  component: DocPdfRouter,
});

type Doc = {
  id: string;
  kind: string;
  title: string;
  content: string | null;
  storage_path: string | null;
  mime_type: string | null;
  created_at: string;
};

type DocImage = { id: string; url: string; caption: string | null };

/**
 * Elektroprosjekter bruker en egen kunde-PDF som kun bygges på utstedte,
 * kundesynlige snapshot-data. Andre moduler beholder den gamle PDF-en.
 */
function DocPdfRouter() {
  const { id: projectId } = Route.useParams();
  const moduleQ = useQuery({
    queryKey: ["project-pdf-module", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("module_key")
        .eq("id", projectId)
        .maybeSingle();
      if (error) throw error;
      return data?.module_key ?? null;
    },
  });

  if (moduleQ.isLoading) {
    return <div className="p-10 text-center text-muted-foreground">Laster…</div>;
  }
  if (moduleQ.data === "electrician") {
    return <ElectroCustomerPdf projectId={projectId} />;
  }
  return <DocPdfView projectId={projectId} />;
}

function DocPdfView({ projectId }: { projectId: string }) {
  const { data: company } = useCompanyProfile();


  const q = useQuery({
    queryKey: ["project-doc-pdf", projectId],
    queryFn: async () => {
      const [projRes, checklistRes, docsRes, closureRes] = await Promise.all([
        supabase
          .from("projects")
          .select("id, name, project_number, description, address, postal_code, city, module_key, customers(name, address, postal_code, city, email, phone)")
          .eq("id", projectId)
          .maybeSingle(),
        supabase
          .from("project_checklist_items")
          .select("label, checked, checked_at")
          .eq("project_id", projectId)
          .order("sort_order"),
        supabase
          .from("project_documents")
          .select("*")
          .eq("project_id", projectId)
          .order("created_at"),
        supabase
          .from("project_closures")
          .select("*")
          .eq("project_id", projectId)
          .maybeSingle(),
      ]);

      const documents = (docsRes.data ?? []) as Doc[];
      const imageDocs = documents.filter(
        (d): d is Doc & { storage_path: string } =>
          !!d.storage_path && (d.mime_type ?? "").startsWith("image/"),
      );

      const { data: befImgs } = await supabase
        .from("project_images")
        .select("id, storage_path, caption")
        .eq("project_id", projectId)
        .order("created_at");
      const befImgRows = befImgs ?? [];

      const images: DocImage[] = [];
      if (imageDocs.length > 0) {
        const { data: signed } = await supabase.storage
          .from("project-images")
          .createSignedUrls(imageDocs.map((d) => d.storage_path), 60 * 60);
        (signed ?? []).forEach((s, idx) => {
          if (s.signedUrl) images.push({ id: imageDocs[idx].id, url: s.signedUrl, caption: imageDocs[idx].title });
        });
      }

      // Include project_images bucket too (befaring)
      if (befImgRows.length > 0) {
        const { data: signed } = await supabase.storage
          .from("project-images")
          .createSignedUrls(befImgRows.map((r) => r.storage_path), 60 * 60);
        (signed ?? []).forEach((s, idx) => {
          if (s.signedUrl) images.push({ id: befImgRows[idx].id, url: s.signedUrl, caption: befImgRows[idx].caption });
        });
      }

      return {
        project: projRes.data as Record<string, unknown> | null,
        checklist: (checklistRes.data ?? []) as Array<{ label: string; checked: boolean; checked_at: string | null }>,
        documents,
        closure: closureRes.data as Record<string, unknown> | null,
        images,
      };
    },
  });

  useEffect(() => {
    if (q.data && company) setTimeout(() => window.print(), 500);
  }, [q.data, company]);

  if (!q.data || !company) {
    return <div className="p-10 text-center text-muted-foreground">Laster…</div>;
  }

  const { project, checklist, documents, closure, images } = q.data;
  const customer = (project?.customers as { name?: string; address?: string; postal_code?: string; city?: string; email?: string; phone?: string } | null) ?? null;
  const accent = company.pdf_accent_color?.trim() || "#111827";
  const footerText = `${company.company_name} • Org.nr ${formatOrgNumber(company.org_number)} • ${formatWebsite(company.website)}`;
  const notes = documents.filter((d) => !d.storage_path);
  const fileDocs = documents.filter((d) => d.storage_path && !(d.mime_type ?? "").startsWith("image/"));

  return (
    <>
      <style>{`
        @page {
          size: A4;
          margin: 22mm 18mm 26mm 18mm;
          @bottom-center {
            content: "${footerText.replace(/"/g, '\\"')} — Side " counter(page) " av " counter(pages);
            font-size: 9pt;
            color: #6b7280;
          }
        }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .avoid-break { break-inside: avoid; page-break-inside: avoid; }
        }
        .pdf-accent { color: ${accent}; }
      `}</style>

      <div className="min-h-screen bg-neutral-100 print:bg-white">
        <div className="no-print sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur">
          <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-3">
            <Link to="/projects/$id/documentation" params={{ id: projectId }} className="btn btn-ghost">
              <ArrowLeft className="h-4 w-4" /> Tilbake
            </Link>
            <button onClick={() => window.print()} className="btn btn-primary">
              <Printer className="h-4 w-4" /> Skriv ut / lagre PDF
            </button>
          </div>
        </div>

        <article className="mx-auto my-8 max-w-[210mm] bg-white text-neutral-900 shadow-xl print:my-0 print:max-w-none print:shadow-none">
          <div className="px-14 py-12 print:p-0">
            <header className="flex items-start justify-between gap-8 border-b-2 pb-8" style={{ borderColor: accent }}>
              <div className="flex items-center gap-4">
                <div className="grid h-16 w-16 place-items-center overflow-hidden rounded-2xl bg-neutral-900">
                  {company.logo_url ? (
                    <img
                      src={company.logo_url}
                      alt={company.company_name}
                      className="h-full w-full object-contain p-1.5"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <span className="text-xs font-semibold uppercase text-white/70">
                      {(company.company_name ?? "?").slice(0, 2)}
                    </span>
                  )}
                </div>
                <div>
                  <div className="text-lg font-semibold tracking-tight">{company.company_name}</div>
                  <div className="text-xs text-neutral-500">
                    Org.nr {formatOrgNumber(company.org_number)}
                  </div>
                  <div className="text-xs text-neutral-500">
                    {formatPhone(company.phone)} · {company.company_email}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] pdf-accent">
                  Prosjektdokumentasjon
                </div>
                <div className="mt-1 font-mono text-sm text-neutral-700">
                  {(project?.project_number as string | null) ?? "—"}
                </div>
                <div className="mt-3 text-[11px] uppercase tracking-wider text-neutral-400">Dato</div>
                <div className="text-sm text-neutral-700">{formatDate(new Date().toISOString())}</div>
              </div>
            </header>

            <section className="grid grid-cols-2 gap-10 py-10">
              <div>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400">Kunde</div>
                {customer ? (
                  <>
                    <div className="text-lg font-semibold">{customer.name}</div>
                    {customer.address && <div className="mt-1 text-sm text-neutral-600">{customer.address}</div>}
                    {(customer.postal_code || customer.city) && (
                      <div className="text-sm text-neutral-600">
                        {customer.postal_code} {customer.city}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-neutral-500">—</div>
                )}
              </div>
              <div>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400">Prosjekt</div>
                <div className="text-lg font-semibold leading-snug">{(project?.name as string) ?? ""}</div>
                {project?.description ? (
                  <p className="mt-2 whitespace-pre-wrap text-sm text-neutral-600">
                    {project.description as string}
                  </p>
                ) : null}
              </div>
            </section>

            {typeof closure?.summary === "string" && closure.summary.trim() && (
              <section className="avoid-break mb-8 rounded-2xl p-6" style={{ background: `${accent}0d` }}>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] pdf-accent">
                  Oppsummering
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-800">
                  {closure.summary as string}
                </p>
              </section>
            )}

            {checklist.length > 0 && (
              <section className="avoid-break mb-8">
                <SectionTitle title="Kontrolliste" />
                <ul className="space-y-1.5">
                  {checklist.map((c, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <span
                        className="mt-0.5 inline-block h-4 w-4 shrink-0 rounded border border-neutral-400 text-center text-xs leading-4"
                        style={c.checked ? { background: accent, color: "white", borderColor: accent } : {}}
                      >
                        {c.checked ? "✓" : ""}
                      </span>
                      <span className={c.checked ? "" : "text-neutral-500"}>{c.label}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {typeof closure?.handover_text === "string" && closure.handover_text.trim() && (
              <section className="avoid-break mb-8">
                <SectionTitle title="Overlevering" />
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-800">
                  {closure.handover_text as string}
                </p>
              </section>
            )}

            {typeof closure?.samsvar_draft === "string" && closure.samsvar_draft.trim() && (
              <section className="avoid-break mb-8 rounded-2xl border border-amber-200 bg-amber-50 p-6">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-800">
                  Utkast til samsvarsdokument
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-800">
                  {closure.samsvar_draft as string}
                </p>
                <div className="mt-3 text-xs text-amber-800">
                  Dette er et utkast. Innholdet må kontrolleres og signeres av ansvarlig person før bruk.
                </div>
              </section>
            )}

            {notes.length > 0 && (
              <section className="avoid-break mb-8">
                <SectionTitle title="Notater" />
                <div className="space-y-3">
                  {notes.map((n) => (
                    <div key={n.id} className="avoid-break rounded border border-neutral-200 p-3 text-sm">
                      <div className="font-medium">{n.title}</div>
                      {n.content && (
                        <p className="mt-1 whitespace-pre-wrap text-neutral-700">{n.content}</p>
                      )}
                      <div className="mt-1 text-xs text-neutral-500">
                        {new Date(n.created_at).toLocaleString("nb-NO")}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {fileDocs.length > 0 && (
              <section className="avoid-break mb-8">
                <SectionTitle title="Vedlegg" />
                <ul className="space-y-1 text-sm text-neutral-700">
                  {fileDocs.map((d) => (
                    <li key={d.id}>• {d.title}</li>
                  ))}
                </ul>
              </section>
            )}

            {images.length > 0 && (
              <section className="mt-4 avoid-break">
                <SectionTitle title="Bilder" />
                <div className="grid grid-cols-2 gap-4">
                  {images.map((img) => (
                    <figure key={img.id} className="avoid-break overflow-hidden rounded-xl border border-neutral-200">
                      <img src={img.url} alt={img.caption ?? ""} className="h-56 w-full object-cover" />
                      {img.caption && (
                        <figcaption className="p-3 text-xs text-neutral-600">{img.caption}</figcaption>
                      )}
                    </figure>
                  ))}
                </div>
              </section>
            )}

            <section className="avoid-break mt-14">
              <div className="text-sm text-neutral-600">Med vennlig hilsen</div>
              {company.signature_url ? (
                <div className="mt-3 h-16">
                  <img
                    src={company.signature_url}
                    alt="Signatur"
                    className="h-full w-auto object-contain"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </div>
              ) : (
                <div className="mt-3 h-8" />
              )}
              <div className="mt-2 border-t border-neutral-300 pt-3 text-sm">
                <div className="font-semibold">{company.company_name}</div>
                <div className="text-neutral-600">Org.nr. {formatOrgNumber(company.org_number)}</div>
                <div className="text-neutral-600">{formatPhone(company.phone)} · {company.company_email}</div>
              </div>
            </section>
          </div>
        </article>
      </div>
    </>
  );
}

function SectionTitle({ title }: { title: string }) {
  return (
    <div className="mb-3 border-b border-neutral-200 pb-2">
      <h2 className="text-sm font-semibold uppercase tracking-[0.15em] text-neutral-500">{title}</h2>
    </div>
  );
}
